import {useMemo} from "react";
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";
import {priceFormat} from "@pg-mono/string-utils";

import {PricesInsightBox} from "../../atoms/PricesInsightBox";
import {getRoomsFlatsLabelWhere} from "../../constants/prices_address_rooms";
import {isAllRooms, RoomsFilter} from "../../constants/rcn_rooms_filter";
import {IRcnStats, useRcnLatestStats} from "../../hooks/use_rcn_latest_stats";
import {useRcnPeriodStats} from "../../hooks/use_rcn_period_stats";
import {PRICES_ADDRESS_ANALYTICS_LOCATION} from "../../tracking/prices_city_analytics";
import type {IPricesAddress, IPricesAddressRegion} from "../../types/IPricesAddress";
import {RcnPriceStatsGrain} from "../../types/IRcnPriceStats";
import type {RcnPriceStatsSeriesPoint} from "../../types/RcnPriceStatsSeriesPoint";
import {formatRcnPointPeriodWithPreposition} from "../../utils/format_rcn_period_label";
import {PricesCityViewSectionTracker} from "../PricesCityViewSectionTracker";
import {IPricesAddressComparisonBar, PricesAddressComparisonBarKind, PricesAddressComparisonChart} from "./PricesAddressComparisonChart";

interface Props {
    address: IPricesAddress;
    statsRegion: IPricesAddressRegion;
    rooms: RoomsFilter;
}

interface IBarCandidate {
    kind: PricesAddressComparisonBarKind;
    region: IPricesAddressRegion | null;
    stats: IRcnStats;
    sublabel: string;
    color: string;
}

interface IInsightParams {
    baseBar: IPricesAddressComparisonBar;
    districtBar: IPricesAddressComparisonBar | null;
    cityBar: IPricesAddressComparisonBar | null;
    district: IPricesAddressRegion | null;
    city: IPricesAddressRegion;
}

export const PricesAddressComparison = (props: Props) => {
    const {address, statsRegion, rooms} = props;

    const districtRegion = address.district;
    const cityRegion = address.city;

    // The building's newest point anchors the chart: the district and the city are read for that same
    // period, in the same grain (whichever of month, quarter or year has the building's latest period), otherwise the bars
    // would compare sales from different periods.
    const baseStats = useRcnLatestStats(address.rcnAddress, rooms, "per_m2");
    const periodStart = baseStats.point?.period_start ?? null;
    const districtStats = useRcnPeriodStats(districtRegion?.id ?? null, rooms, "per_m2", periodStart, baseStats.grain);
    const cityStats = useRcnPeriodStats(cityRegion?.id ?? null, rooms, "per_m2", periodStart, baseStats.grain);

    const isLoading = baseStats.isLoading || districtStats.isLoading || cityStats.isLoading;

    const bars = useMemo(
        () =>
            (
                [
                    {
                        kind: "okolica",
                        region: {...statsRegion, short_name: address.name},
                        stats: baseStats,
                        sublabel: "ten budynek · z aktów notarialnych",
                        color: "var(--colors-info)"
                    },
                    {kind: "dzielnica", region: districtRegion, stats: districtStats, sublabel: "mediana dzielnicy", color: "var(--colors-success)"},
                    {kind: "miasto", region: cityRegion, stats: cityStats, sublabel: "mediana miasta", color: "var(--colors-highlight)"}
                ] satisfies IBarCandidate[]
            )
                .map(toBar)
                .filter((bar): bar is IPricesAddressComparisonBar => bar !== null),
        [address.name, statsRegion, districtRegion, cityRegion, baseStats, districtStats, cityStats]
    );

    const baseBar = bars.find((bar) => bar.kind === "okolica") ?? null;
    const districtBar = bars.find((bar) => bar.kind === "dzielnica") ?? null;
    const cityBar = bars.find((bar) => bar.kind === "miasto") ?? null;
    const hasComparison = baseBar !== null && bars.length >= 2;

    if (!isLoading && !hasComparison) {
        return null;
    }

    const insight = hasComparison && baseBar ? getComparisonInsight({baseBar, districtBar, cityBar, district: districtRegion, city: address.city}) : null;

    const periodNote = hasComparison && baseBar && baseStats.point && baseStats.grain ? getPeriodNote(baseStats.point, baseStats.grain, rooms) : null;

    return (
        <div className={sectionStyle}>
            <Container className={containerStyle}>
                <PricesCityViewSectionTracker sectionName="address_comparison" location={PRICES_ADDRESS_ANALYTICS_LOCATION} />

                <div>
                    <Text variant="headline_3" as="h2" mb={1}>
                        Czy to drogo na tę okolicę?
                    </Text>

                    <Text variant="body_copy_1" color="var(--colors-gray-700)">
                        Porównanie mediany ceny za metr {getRoomsFlatsLabelWhere(rooms)} w tym budynku z dzielnicą i całym miastem.
                    </Text>
                </div>

                <div className={chartBlockStyle}>
                    <PricesAddressComparisonChart bars={hasComparison ? bars : []} isLoading={isLoading} />

                    {periodNote && (
                        <Text variant="info_txt_1" color="var(--colors-gray-600)">
                            {periodNote}
                        </Text>
                    )}
                </div>

                {insight && <PricesInsightBox>{insight}</PricesInsightBox>}
            </Container>
        </div>
    );
};

const toBar = (candidate: IBarCandidate): IPricesAddressComparisonBar | null => {
    const {kind, region, stats, sublabel, color} = candidate;

    if (!region || !stats.point || stats.point.value <= 0) {
        return null;
    }

    return {
        kind,
        label: region.short_name,
        sublabel,
        color,
        value: Math.round(stats.point.value),
        nRows: stats.point.n_rows,
        periodStart: stats.point.period_start,
        periodLabel: stats.point.period_label
    };
};

const GRAIN_GENITIVE: Record<RcnPriceStatsGrain, string> = {month: "miesiąca", quarter: "kwartału", year: "roku"};

// The anchor period is the newest one published for the selected size group, which on a size tab can be
// older than the building's newest period in another group - so the note names the group, not the building.
const getPeriodNote = (point: RcnPriceStatsSeriesPoint, grain: RcnPriceStatsGrain, rooms: RoomsFilter): string => {
    const scope = isAllRooms(rooms) ? "transakcje w tym budynku" : `transakcje ${getRoomsFlatsLabelWhere(rooms)} w tym budynku`;

    return `Wszystkie wartości pochodzą ${formatRcnPointPeriodWithPreposition(point)} – ostatniego ${GRAIN_GENITIVE[grain]}, za który rejestr opublikował ${scope}.`;
};

const getPriceWord = (difference: number): string => (difference < 0 ? "tańszy" : "droższy");

const getDistrictClause = (difference: number, district: IPricesAddressRegion): string =>
    difference === 0
        ? `kosztuje tyle samo, co mediana ${district.name_declension_what}`
        : `jest o ${priceFormat(Math.abs(difference), {precision: 0})} ${getPriceWord(difference)} niż mediana ${district.name_declension_what}`;

const getCityClause = (difference: number, city: IPricesAddressRegion): string =>
    difference === 0
        ? `kosztuje tyle samo, co mediana dla całego miasta (${city.short_name})`
        : `jest o ${priceFormat(Math.abs(difference), {precision: 0})} ${getPriceWord(difference)} niż mediana dla całego miasta (${city.short_name})`;

const getCityPart = (difference: number, city: IPricesAddressRegion, districtDifference: number): string => {
    if (difference === 0) {
        return districtDifference === 0 ? ` i całego miasta (${city.short_name})` : ` i kosztuje tyle samo, co mediana dla całego miasta (${city.short_name})`;
    }

    const verb = districtDifference === 0 ? "jest " : "";

    return ` i ${verb}o ${priceFormat(Math.abs(difference), {precision: 0})} ${getPriceWord(difference)} niż w całym mieście (${city.short_name})`;
};

const getComparisonInsight = (params: IInsightParams): string | null => {
    const {baseBar, districtBar, cityBar, district, city} = params;

    const cityDifference = cityBar ? baseBar.value - cityBar.value : null;

    if (district && districtBar) {
        const districtDifference = baseBar.value - districtBar.value;
        const cityPart = cityDifference === null ? "" : getCityPart(cityDifference, city, districtDifference);

        return `Metr w tym budynku ${getDistrictClause(districtDifference, district)}${cityPart}.`;
    }

    if (cityDifference !== null) {
        return `Metr w tym budynku ${getCityClause(cityDifference, city)}.`;
    }

    return null;
};

const sectionStyle = css({
    width: "100%",
    py: 3,
    backgroundColor: "var(--colors-white)",

    md: {
        py: 8
    }
});

const containerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 3,
    maxWidth: "1168px"
});

const chartBlockStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});
import {useMemo} from "react";
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";
import {priceFormat} from "@pg-mono/string-utils";

import {PricesInsightBox} from "../../atoms/PricesInsightBox";
import {getRoomsFlatsLabelWhere} from "../../constants/prices_address_rooms";
import {isAllRooms, RoomsFilter} from "../../constants/rcn_rooms_filter";
import {IRcnStats, useRcnLatestStats} from "../../hooks/use_rcn_latest_stats";
import {useRcnPeriodStats} from "../../hooks/use_rcn_period_stats";
import {PRICES_ADDRESS_ANALYTICS_LOCATION} from "../../tracking/prices_city_analytics";
import type {IPricesAddress, IPricesAddressRegion} from "../../types/IPricesAddress";
import {RcnPriceStatsGrain} from "../../types/IRcnPriceStats";
import type {RcnPriceStatsSeriesPoint} from "../../types/RcnPriceStatsSeriesPoint";
import {formatRcnPointPeriodWithPreposition} from "../../utils/format_rcn_period_label";
import {PricesCityViewSectionTracker} from "../PricesCityViewSectionTracker";
import {IPricesAddressComparisonBar, PricesAddressComparisonBarKind, PricesAddressComparisonChart} from "./PricesAddressComparisonChart";

interface Props {
    address: IPricesAddress;
    statsRegion: IPricesAddressRegion;
    rooms: RoomsFilter;
}

interface IBarCandidate {
    kind: PricesAddressComparisonBarKind;
    region: IPricesAddressRegion | null;
    stats: IRcnStats;
    sublabel: string;
    color: string;
}

interface IInsightParams {
    baseBar: IPricesAddressComparisonBar;
    districtBar: IPricesAddressComparisonBar | null;
    cityBar: IPricesAddressComparisonBar | null;
    district: IPricesAddressRegion | null;
    city: IPricesAddressRegion;
}

export const PricesAddressComparison = (props: Props) => {
    const {address, statsRegion, rooms} = props;

    const districtRegion = address.district;
    const cityRegion = address.city;

    // The building's newest point anchors the chart: the district and the city are read for that same
    // period, in the same grain (whichever of month, quarter or year has the building's latest period), otherwise the bars
    // would compare sales from different periods.
    const baseStats = useRcnLatestStats(address.rcnAddress, rooms, "per_m2");
    const periodStart = baseStats.point?.period_start ?? null;
    const districtStats = useRcnPeriodStats(districtRegion?.id ?? null, rooms, "per_m2", periodStart, baseStats.grain);
    const cityStats = useRcnPeriodStats(cityRegion?.id ?? null, rooms, "per_m2", periodStart, baseStats.grain);

    const isLoading = baseStats.isLoading || districtStats.isLoading || cityStats.isLoading;

    const bars = useMemo(
        () =>
            (
                [
                    {
                        kind: "okolica",
                        region: {...statsRegion, short_name: address.name},
                        stats: baseStats,
                        sublabel: "ten budynek · z aktów notarialnych",
                        color: "var(--colors-info)"
                    },
                    {kind: "dzielnica", region: districtRegion, stats: districtStats, sublabel: "mediana dzielnicy", color: "var(--colors-success)"},
                    {kind: "miasto", region: cityRegion, stats: cityStats, sublabel: "mediana miasta", color: "var(--colors-highlight)"}
                ] satisfies IBarCandidate[]
            )
                .map(toBar)
                .filter((bar): bar is IPricesAddressComparisonBar => bar !== null),
        [address.name, statsRegion, districtRegion, cityRegion, baseStats, districtStats, cityStats]
    );

    const baseBar = bars.find((bar) => bar.kind === "okolica") ?? null;
    const districtBar = bars.find((bar) => bar.kind === "dzielnica") ?? null;
    const cityBar = bars.find((bar) => bar.kind === "miasto") ?? null;
    const hasComparison = baseBar !== null && bars.length >= 2;

    if (!isLoading && !hasComparison) {
        return null;
    }

    const insight = hasComparison && baseBar ? getComparisonInsight({baseBar, districtBar, cityBar, district: districtRegion, city: address.city}) : null;

    const periodNote = hasComparison && baseBar && baseStats.point && baseStats.grain ? getPeriodNote(baseStats.point, baseStats.grain, rooms) : null;

    return (
        <div className={sectionStyle}>
            <Container className={containerStyle}>
                <PricesCityViewSectionTracker sectionName="address_comparison" location={PRICES_ADDRESS_ANALYTICS_LOCATION} />

                <div>
                    <Text variant="headline_3" as="h2" mb={1}>
                        Czy to drogo na tę okolicę?
                    </Text>

                    <Text variant="body_copy_1" color="var(--colors-gray-700)">
                        Porównanie mediany ceny za metr {getRoomsFlatsLabelWhere(rooms)} w tym budynku z dzielnicą i całym miastem.
                    </Text>
                </div>

                <div className={chartBlockStyle}>
                    <PricesAddressComparisonChart bars={hasComparison ? bars : []} isLoading={isLoading} />

                    {periodNote && (
                        <Text variant="info_txt_1" color="var(--colors-gray-600)">
                            {periodNote}
                        </Text>
                    )}
                </div>

                {insight && <PricesInsightBox>{insight}</PricesInsightBox>}
            </Container>
        </div>
    );
};

const toBar = (candidate: IBarCandidate): IPricesAddressComparisonBar | null => {
    const {kind, region, stats, sublabel, color} = candidate;

    if (!region || !stats.point || stats.point.value <= 0) {
        return null;
    }

    return {
        kind,
        label: region.short_name,
        sublabel,
        color,
        value: Math.round(stats.point.value),
        nRows: stats.point.n_rows,
        periodStart: stats.point.period_start,
        periodLabel: stats.point.period_label
    };
};

const GRAIN_GENITIVE: Record<RcnPriceStatsGrain, string> = {month: "miesiąca", quarter: "kwartału", year: "roku"};

// The anchor period is the newest one published for the selected size group, which on a size tab can be
// older than the building's newest period in another group - so the note names the group, not the building.
const getPeriodNote = (point: RcnPriceStatsSeriesPoint, grain: RcnPriceStatsGrain, rooms: RoomsFilter): string => {
    const scope = isAllRooms(rooms) ? "transakcje w tym budynku" : `transakcje ${getRoomsFlatsLabelWhere(rooms)} w tym budynku`;

    return `Wszystkie wartości pochodzą ${formatRcnPointPeriodWithPreposition(point)} – ostatniego ${GRAIN_GENITIVE[grain]}, za który rejestr opublikował ${scope}.`;
};

const getPriceWord = (difference: number): string => (difference < 0 ? "tańszy" : "droższy");

const getDistrictClause = (difference: number, district: IPricesAddressRegion): string =>
    difference === 0
        ? `kosztuje tyle samo, co mediana ${district.name_declension_what}`
        : `jest o ${priceFormat(Math.abs(difference), {precision: 0})} ${getPriceWord(difference)} niż mediana ${district.name_declension_what}`;

const getCityClause = (difference: number, city: IPricesAddressRegion): string =>
    difference === 0
        ? `kosztuje tyle samo, co mediana dla całego miasta (${city.short_name})`
        : `jest o ${priceFormat(Math.abs(difference), {precision: 0})} ${getPriceWord(difference)} niż mediana dla całego miasta (${city.short_name})`;

const getCityPart = (difference: number, city: IPricesAddressRegion, districtDifference: number): string => {
    if (difference === 0) {
        return districtDifference === 0 ? ` i całego miasta (${city.short_name})` : ` i kosztuje tyle samo, co mediana dla całego miasta (${city.short_name})`;
    }

    const verb = districtDifference === 0 ? "jest " : "";

    return ` i ${verb}o ${priceFormat(Math.abs(difference), {precision: 0})} ${getPriceWord(difference)} niż w całym mieście (${city.short_name})`;
};

const getComparisonInsight = (params: IInsightParams): string | null => {
    const {baseBar, districtBar, cityBar, district, city} = params;

    const cityDifference = cityBar ? baseBar.value - cityBar.value : null;

    if (district && districtBar) {
        const districtDifference = baseBar.value - districtBar.value;
        const cityPart = cityDifference === null ? "" : getCityPart(cityDifference, city, districtDifference);

        return `Metr w tym budynku ${getDistrictClause(districtDifference, district)}${cityPart}.`;
    }

    if (cityDifference !== null) {
        return `Metr w tym budynku ${getCityClause(cityDifference, city)}.`;
    }

    return null;
};

const sectionStyle = css({
    width: "100%",
    py: 3,
    backgroundColor: "var(--colors-white)",

    md: {
        py: 8
    }
});

const containerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 3,
    maxWidth: "1168px"
});

const chartBlockStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});
